import { NavLink, useNavigate, Link } from "react-router-dom";
import { LayoutDashboard, CalendarDays, Inbox, Users, Images, Settings, LogOut, ExternalLink, FileText } from "lucide-react";
import { useAuth } from "../../context/AuthContext";

const LINKS = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/submissions", label: "Submissions", icon: FileText },
  { to: "/admin/bookings", label: "Bookings", icon: Inbox },
  { to: "/admin/calendar", label: "Calendar", icon: CalendarDays },
  { to: "/admin/artists", label: "Artists", icon: Users },
  { to: "/admin/portfolio", label: "Portfolio", icon: Images },
  { to: "/admin/settings", label: "Settings", icon: Settings },
];

export function AdminLayout({ children, title }) {
  const { logout } = useAuth();
  const nav = useNavigate();
  const doLogout = async () => { await logout(); nav("/admin/login", { replace: true }); };

  return (
    <div className="min-h-screen bg-obsidian flex">
      {/* sidebar */}
      <aside className="hidden lg:flex flex-col w-64 border-r border-ash bg-charcoal fixed inset-y-0">
        <div className="p-6 border-b border-ash">
          <Link to="/admin" className="font-serif text-2xl text-white">Obsidian</Link>
          <p className="eyebrow mt-1">Studio Console</p>
        </div>
        <nav className="flex-1 p-4 space-y-1">
          {LINKS.map((l) => (
            <NavLink key={l.to} to={l.to} end={l.end} data-testid={`admin-nav-${l.label.toLowerCase()}`}
              className={({ isActive }) => `flex items-center gap-3 px-4 py-3 font-mono text-[0.7rem] uppercase tracking-[0.15em] transition-colors ${isActive ? "bg-ember text-obsidian" : "text-[#C4C4CC] hover:bg-elevated hover:text-white"}`}>
              <l.icon size={16} /> {l.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-ash space-y-1">
          <a href="/" target="_blank" rel="noreferrer" className="flex items-center gap-3 px-4 py-3 font-mono text-[0.7rem] uppercase tracking-[0.15em] text-[#828290] hover:text-white transition-colors"><ExternalLink size={16} /> View Site</a>
          <button data-testid="admin-logout" onClick={doLogout} className="w-full flex items-center gap-3 px-4 py-3 font-mono text-[0.7rem] uppercase tracking-[0.15em] text-[#828290] hover:text-red-400 transition-colors"><LogOut size={16} /> Logout</button>
        </div>
      </aside>

      {/* mobile top bar */}
      <div className="lg:hidden fixed top-0 inset-x-0 z-40 bg-charcoal border-b border-ash flex items-center justify-between px-4 h-14">
        <Link to="/admin" className="font-serif text-xl text-white">Obsidian</Link>
        <button onClick={doLogout} className="text-[#828290]"><LogOut size={18} /></button>
      </div>
      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-charcoal border-t border-ash grid grid-cols-7">
        {LINKS.map((l) => (
          <NavLink key={l.to} to={l.to} end={l.end} className={({ isActive }) => `flex flex-col items-center gap-0.5 py-2.5 ${isActive ? "text-ember" : "text-[#828290]"}`}>
            <l.icon size={16} />
          </NavLink>
        ))}
      </nav>

      <div className="flex-1 lg:ml-64 pt-14 lg:pt-0 pb-20 lg:pb-0">
        <header className="border-b border-ash px-6 lg:px-10 py-8">
          <p className="eyebrow mb-2">Obsidian Studio</p>
          <h1 className="font-serif text-4xl text-white">{title}</h1>
        </header>
        <div className="p-6 lg:p-10">{children}</div>
      </div>
    </div>
  );
}
